'use client';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Confetti from 'react-confetti';
import { postContact } from '../api';
import indianOffice from '../../assets/images/indian-office.jpg';
import uaeOffice from '../../assets/images/uae_pic.jpg';
import MetaUpdater from '../metadata/MetaUpdater';
import whatsappIcon from '../../assets/images/whatsapp_icon.svg';

interface FormData {
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  phone: string;
  message: string;
  type: string;
  captcha: string;
}

interface Errors {
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  phone: string;
  message: string;
  type: string;
  captcha: string;
}

const generateCaptcha = () => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 6; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
};

const Contact: React.FC = () => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    name: '',
    email: '',
    phone: '',
    message: '',
    type: '',
    captcha: '',
  });

  const [errors, setErrors] = useState<Errors>({
    firstName: '',
    lastName: '',
    name: '',
    email: '',
    phone: '',
    message: '',
    type: '',
    captcha: '',
  });

  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const [captchaCode, setCaptchaCode] = useState<string>(generateCaptcha());
  const [showConfetti, setShowConfetti] = useState<boolean>(false);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
    setSubmitStatus({ type: null, message: '' });
  };

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const sanitizeInput = (str: string) => {
    return str
      .replace(/<script.*?>.*?<\/script>/gi, '')
      .replace(/<\/?[^>]+(>|$)/g, '');
  };

  const refreshCaptcha = () => {
    setCaptchaCode(generateCaptcha());
    setFormData({ ...formData, captcha: '' });
    setErrors({ ...errors, captcha: '' });
  };

  const handleSubmit = async () => {
    let valid = true;
    let newErrors: Errors = {
      firstName: '',
      lastName: '',
      name: '',
      email: '',
      phone: '',
      message: '',
      type: '',
      captcha: '',
    };

    if (!formData.firstName.trim()) {
      newErrors.firstName = t('first_name_required');
      valid = false;
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = t('last_name_required');
      valid = false;
    }

    if (!formData.email.trim()) {
      newErrors.email = t('email_required');
      valid = false;
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t('email_invalid');
      valid = false;
    }

    if (!formData.phone.trim()) {
      newErrors.phone = t('phone_required');
      valid = false;
    }

    if (!formData.captcha.trim()) {
      newErrors.captcha = t('captcha_required');
      valid = false;
    } else if (formData.captcha !== captchaCode) {
      newErrors.captcha = t('captcha_invalid');
      valid = false;
    }

    setErrors(newErrors);

    if (valid) {
      try {
        const sanitizedData = {
          name: sanitizeInput(`${formData.firstName} ${formData.lastName}`),
          email: sanitizeInput(formData.email),
          phone: sanitizeInput(formData.phone),
          message: sanitizeInput(formData.message),
          type: sanitizeInput(formData.type),
        };

        await postContact(sanitizedData);

        setShowConfetti(true);
        setSubmitStatus({
          type: 'success',
          message: t('form_success_message'),
        });

        setFormData({
          firstName: '',
          lastName: '',
          name: '',
          email: '',
          phone: '',
          message: '',
          type: '',
          captcha: '',
        });
        setCaptchaCode(generateCaptcha());

        setTimeout(() => setShowConfetti(false), 5000);
      } catch (error: any) {
        setSubmitStatus({
          type: 'error',
          message: t('form_error_message'),
        });
      }
    }
  };

  return (
    <>
      <MetaUpdater page="contact" />
      {showConfetti && <Confetti />}
      <section
        className="contact_image"
        dir={t('language') === 'ar' ? 'rtl' : 'ltr'}
      >
        <div className="container">
          <div className="row">
            <div className="col-md-12 text-center">
              <div className="main-title">
                <h2>{t('contact_title')}</h2>
                <p>{t('contact_subtitle')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        className="contact-details"
        dir={t('language') === 'ar' ? 'rtl' : 'ltr'}
      >
        <div className="container">
          <div className="row">
            <div className="col-md-6 mb-4">
              <div className="card h-100">
                <img
                  src={indianOffice}
                  className="card-img-top"
                  alt={t('india_office')}
                  style={{ height: '350px' }}
                />
                <div className="card-body">
                  <h5 className="card-title text-center mb-4">
                    {t('india_office')}
                  </h5>
                  <div className="d-flex mb-3 align-items-start">
                    <i
                      className="bi bi-geo-alt-fill"
                      style={{
                        color: '#DB2217',
                        fontSize: '1.2rem',
                        marginRight: t('language') === 'ar' ? '0' : '10px',
                        marginLeft: t('language') === 'ar' ? '10px' : '0',
                      }}
                    ></i>
                    <div className="mb-3">
                      <a
                        href="https://maps.app.goo.gl/LyHuiMJtosk4ZDub6"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-decoration-none text-dark"
                      >
                        <p className="mb-0 fw-semibold">{t('india_address')}</p>
                      </a>
                    </div>
                  </div>
                  <div className="d-flex mb-3 align-items-center">
                    <i
                      className="bi bi-envelope-fill"
                      style={{
                        color: '#DB2217',
                        fontSize: '1.2rem',
                        marginRight: t('language') === 'ar' ? '0' : '10px',
                        marginLeft: t('language') === 'ar' ? '10px' : '0',
                      }}
                    ></i>
                    <p className="mb-0">
                      <a
                        href="mailto:info@shahlpo.ae"
                        className="text-decoration-none text-dark fw-semibold"
                      >
                        {t('email')}
                      </a>
                    </p>
                  </div>
                  <div className="d-flex mb-3 align-items-center">
                    <i
                      className="bi bi-telephone-fill"
                      style={{
                        color: '#DB2217',
                        fontSize: '1.2rem',
                        marginRight: t('language') === 'ar' ? '0' : '10px',
                        marginLeft: t('language') === 'ar' ? '10px' : '0',
                      }}
                    ></i>
                    <a
                      href="tel:+91 9904000230"
                      className="text-decoration-none text-dark"
                    >
                      <p className="mb-0 fw-semibold"> +91 99040 00230</p>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-6 mb-4">
              <div className="card h-100">
                <img
                  src={uaeOffice}
                  className="card-img-top"
                  alt={t('uae_office')}
                  style={{ height: '350px' }}
                />
                <div className="card-body">
                  <h5 className="card-title text-center mb-4">
                    {t('uae_office')}
                  </h5>
                  <div className="d-flex mb-3 align-items-start">
                    <i
                      className="bi bi-geo-alt-fill"
                      style={{
                        color: '#DB2217',
                        fontSize: '1.2rem',
                        marginRight: t('language') === 'ar' ? '0' : '10px',
                        marginLeft: t('language') === 'ar' ? '10px' : '0',
                      }}
                    ></i>
                    <div>
                      <a
                        href="https://maps.app.goo.gl/dpdpCi9gf8hyVyqcA"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-decoration-none text-dark"
                      >
                        <p className="mb-0 fw-semibold">{t('uae_address')}</p>
                      </a>
                    </div>
                  </div>
                  <div className="d-flex mb-3 align-items-center">
                    <i
                      className="bi bi-envelope-fill"
                      style={{
                        color: '#DB2217',
                        fontSize: '1.2rem',
                        marginRight: t('language') === 'ar' ? '0' : '10px',
                        marginLeft: t('language') === 'ar' ? '10px' : '0',
                      }}
                    ></i>
                    <p className="mb-0">
                      <a
                        href="mailto:info@shahlpo.ae"
                        className="text-decoration-none text-dark fw-semibold"
                      >
                        {t('email')}
                      </a>
                    </p>
                  </div>
                  <div className="d-flex mb-3 align-items-center">
                    <i
                      className="bi bi-telephone-fill"
                      style={{
                        color: '#DB2217',
                        fontSize: '1.2rem',
                        marginRight: t('language') === 'ar' ? '0' : '10px',
                        marginLeft: t('language') === 'ar' ? '10px' : '0',
                      }}
                    ></i>
                    <a
                      href="tel:+971585816688"
                      className="text-decoration-none text-dark"
                    >
                      <p className="mb-0 fw-semibold">+971-585703027</p>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="faq_sec" dir={t('language') === 'ar' ? 'rtl' : 'ltr'}>
        <div className="container">
          <div className="contactInfo align-content-center justify-content-center">
            <h2>{t('contact_info_title')}</h2>
            <ul className="info">
              <li>
                <span>
                  <i className="bi bi-envelope-fill"></i>
                </span>
                <a
                  href="mailto:info@shahlpo.ae"
                  className="text-decoration-none text-white"
                >
                  info@shahlpo.ae
                </a>
              </li>
              <li>
                <span>
                  <i className="bi bi-telephone-fill"></i>
                </span>
                <a
                  href="tel:+971585816688"
                  className="text-decoration-none text-white"
                >
                  <p>+971-585703027</p>
                </a>
              </li>
            </ul>
            <div className="social-card mt-4 bg-blue">
              <div
                className="icon-wrapper facebook-bg"
                style={{ height: '31px', width: '31px' }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  shape-rendering="geometricPrecision"
                  text-rendering="geometricPrecision"
                  image-rendering="optimizeQuality"
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  viewBox="0 0 509 509"
                >
                  <g fill-rule="nonzero">
                    <path
                      fill="#0866FF"
                      d="M509 254.5C509 113.94 395.06 0 254.5 0S0 113.94 0 254.5C0 373.86 82.17 474 193.02 501.51V332.27h-52.48V254.5h52.48v-33.51c0-86.63 39.2-126.78 124.24-126.78 16.13 0 43.95 3.17 55.33 6.33v70.5c-6.01-.63-16.44-.95-29.4-.95-41.73 0-57.86 15.81-57.86 56.91v27.5h83.13l-14.28 77.77h-68.85v174.87C411.35 491.92 509 384.62 509 254.5z"
                    />
                    <path
                      fill="#fff"
                      d="M354.18 332.27l14.28-77.77h-83.13V227c0-41.1 16.13-56.91 57.86-56.91 12.96 0 23.39.32 29.4.95v-70.5c-11.38-3.16-39.2-6.33-55.33-6.33-85.04 0-124.24 40.16-124.24 126.78v33.51h-52.48v77.77h52.48v169.24c19.69 4.88 40.28 7.49 61.48 7.49 10.44 0 20.72-.64 30.83-1.86V332.27h68.85z"
                    />
                  </g>
                </svg>
              </div>
              <div className="icon-wrapper twitter-bg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  shape-rendering="geometricPrecision"
                  text-rendering="geometricPrecision"
                  image-rendering="optimizeQuality"
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  viewBox="0 0 512 512"
                >
                  <path d="M256 0c141.385 0 256 114.615 256 256S397.385 512 256 512 0 397.385 0 256 114.615 0 256 0z" />
                  <path
                    fill="#fff"
                    fill-rule="nonzero"
                    d="M318.64 157.549h33.401l-72.973 83.407 85.85 113.495h-67.222l-52.647-68.836-60.242 68.836h-33.423l78.052-89.212-82.354-107.69h68.924l47.59 62.917 55.044-62.917zm-11.724 176.908h18.51L205.95 176.493h-19.86l120.826 157.964z"
                  />
                </svg>
              </div>
              <div className="icon-wrapper ">
                <div className="whatsapp_deta whatsapp-icon">
                  <a
                    href="https://wa.me/971585703027?text=Hi%2C%20I%20would%20like%20to%20book%20a%20consultation."
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={whatsappIcon}
                      alt="WhatsApp"
                      style={{ width: '32px', height: '32px' }}
                    />
                  </a>
                </div>
              </div>
              <div className="icon-wrapper linkedin-bg">
                <svg
                  className="w-6 h-6 text-white dark:text-white"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    fill-rule="evenodd"
                    d="M12.51 8.796v1.697a3.738 3.738 0 0 1 3.288-1.684c3.455 0 4.202 2.16 4.202 4.97V19.5h-3.2v-5.072c0-1.21-.244-2.766-2.128-2.766-1.827 0-2.139 1.317-2.139 2.676V19.5h-3.19V8.796h3.168ZM7.2 6.106a1.61 1.61 0 0 1-.988 1.483 1.595 1.595 0 0 1-1.743-.348A1.607 1.607 0 0 1 5.6 4.5a1.601 1.601 0 0 1 1.6 1.606Z"
                    clip-rule="evenodd"
                  />
                  <path d="M7.2 8.809H4V19.5h3.2V8.809Z" />
                </svg>
              </div>
              <div className="icon-wrapper thread-bg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  shape-rendering="geometricPrecision"
                  text-rendering="geometricPrecision"
                  image-rendering="optimizeQuality"
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  viewBox="0 0 512 512"
                  className="rounded-5"
                >
                  <path d="M105 0h302c57.75 0 105 47.25 105 105v302c0 57.75-47.25 105-105 105H105C47.25 512 0 464.75 0 407V105C0 47.25 47.25 0 105 0z" />
                  <path
                    fill="#fff"
                    fill-rule="nonzero"
                    d="M337.36 243.58c-1.46-.7-2.95-1.38-4.46-2.02-2.62-48.36-29.04-76.05-73.41-76.33-25.6-.17-48.52 10.27-62.8 31.94l24.4 16.74c10.15-15.4 26.08-18.68 37.81-18.68h.4c14.61.09 25.64 4.34 32.77 12.62 5.19 6.04 8.67 14.37 10.39 24.89-12.96-2.2-26.96-2.88-41.94-2.02-42.18 2.43-69.3 27.03-67.48 61.21.92 17.35 9.56 32.26 24.32 42.01 12.48 8.24 28.56 12.27 45.26 11.35 22.07-1.2 39.37-9.62 51.45-25.01 9.17-11.69 14.97-26.84 17.53-45.92 10.51 6.34 18.3 14.69 22.61 24.73 7.31 17.06 7.74 45.1-15.14 67.96-20.04 20.03-44.14 28.69-80.55 28.96-40.4-.3-70.95-13.26-90.81-38.51-18.6-23.64-28.21-57.79-28.57-101.5.36-43.71 9.97-77.86 28.57-101.5 19.86-25.25 50.41-38.21 90.81-38.51 40.68.3 71.76 13.32 92.39 38.69 10.11 12.44 17.73 28.09 22.76 46.33l28.59-7.63c-6.09-22.45-15.67-41.8-28.72-57.85-26.44-32.53-65.1-49.19-114.92-49.54h-.2c-49.72.35-87.96 17.08-113.64 49.73-22.86 29.05-34.65 69.48-35.04 120.16v.24c.39 50.68 12.18 91.11 35.04 120.16 25.68 32.65 63.92 49.39 113.64 49.73h.2c44.2-.31 75.36-11.88 101.03-37.53 33.58-33.55 32.57-75.6 21.5-101.42-7.94-18.51-23.08-33.55-43.79-43.48zm-76.32 71.76c-18.48 1.04-37.69-7.26-38.64-25.03-.7-13.18 9.38-27.89 39.78-29.64 3.48-.2 6.9-.3 10.25-.3 11.04 0 21.37 1.07 30.76 3.13-3.5 43.74-24.04 50.84-42.15 51.84z"
                  />
                </svg>
              </div>

              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="icon-wrapper instagram-bg"
              >
                <svg
                  className="icon-svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="1.5"
                >
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                  <circle cx="12" cy="12" r="4" />
                  <circle cx="18" cy="6" r="1" />
                </svg>
              </a>
            </div>
          </div>

          <div className="contactForm">
            <h2>{t('get_in_touch')}</h2>
            <div className="formBox">
              <div
                className={`inputBox w50 ${formData.firstName ? 'filled' : ''}`}
              >
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder=" "
                />
                <span>{t('first_name')}</span>
                {errors.firstName && t('first_name_required') && (
                  <small className="text-danger">{errors.firstName}</small>
                )}
              </div>

              <div
                className={`inputBox w50 ${formData.lastName ? 'filled' : ''}`}
              >
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder=" "
                />
                <span>{t('last_name')}</span>
                {errors.lastName && (
                  <small className="text-danger">{errors.lastName}</small>
                )}
              </div>

              <div className={`inputBox w50 ${formData.email ? 'filled' : ''}`}>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder=" "
                />
                <span>{t('email_address')}</span>
                {errors.email && (
                  <small className="text-danger">{errors.email}</small>
                )}
              </div>

              <div className={`inputBox w50 ${formData.phone ? 'filled' : ''}`}>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder=" "
                />
                <span>{t('mobile_number')}</span>
                {errors.phone && (
                  <small className="text-danger">{errors.phone}</small>
                )}
              </div>

              <div
                className={`inputBox w100 ${formData.message ? 'filled' : ''}`}
              >
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder=" "
                ></textarea>
                <span>{t('message_placeholder')}</span>
                {errors.message && (
                  <small className="text-danger">{errors.message}</small>
                )}
              </div>

              <div className="inputBox w100">
                <div className="captcha-line d-flex align-items-center gap-1">
                  <div className="captcha-code-box">{captchaCode}</div>
                  <button
                    type="button"
                    className="refresh-btn"
                    onClick={refreshCaptcha}
                    title={t('refresh_captcha')}
                  >
                    <i className="bi bi-arrow-repeat"></i>
                  </button>
                  <input
                    type="text"
                    name="captcha"
                    value={formData.captcha}
                    onChange={handleChange}
                    className="captcha-input"
                    placeholder={t('captcha_placeholder')}
                  />
                </div>
                {errors.captcha && (
                  <small className="text-danger">{errors.captcha}</small>
                )}
              </div>

              <div className="w-100" style={{ textAlign: 'end' }}>
                <input
                  type="submit"
                  value={t('submit')}
                  onClick={handleSubmit}
                  style={{
                    background: '#082341',
                    borderColor: '#082341',
                    color: '#fff',
                    borderRadius: '10px',
                    padding: '12px 20px',
                    fontSize: '16px',
                    fontWeight: '600',
                  }}
                />
              </div>

              {submitStatus.type && (
                <div
                  className={`alert alert-${submitStatus.type}`}
                  role="alert"
                >
                  {submitStatus.message}
                </div>
              )}
            </div>
          </div>
        </div>
        <style>{`
          .faq_sec {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            background: #082341;
            position: relative;
          }

          .faq_sec::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            width: 50%;
            height: 100%;
            background: #db2217;
          }

          .faq_sec .container {
            position: relative;
            min-width: 1100px;
            min-height: 550px;
            display: flex;
            z-index: 1000;
          }

          .faq_sec .container .contactInfo {
            position: absolute;
            top: 60px;
            width: 350px;
            height: calc(100% - 150px);
            background: #082341;
            z-index: 1;
            padding: 30px;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            box-shadow: 0 20px 25px rgba(0, 0, 0, 0.15);
          }

          .faq_sec .container .contactInfo h2 {
            color: #fff;
            font-size: 24px;
            font-weight: 500;
          }

          .faq_sec .container .contactInfo ul.info {
            position: relative;
            margin: 20px 0;
            padding-left:0px;
          }

          .faq_sec .container .contactInfo ul.info li {
            position: relative;
            list-style: none;
            display: flex;
            margin: 20px 0;
            cursor: pointer;
            align-items: flex-start;
          }

          .faq_sec .container .contactInfo ul.info li span:nth-child(1) {
            width: 30px;
            min-width: 30px;
          }

          .faq_sec .container .contactInfo ul.info li span:nth-child(1) i {
            color: #fff;
            font-size: 1.2rem;
          }

          .faq_sec .container .contactInfo ul.info li span:nth-child(2) {
            color: #fff;
            margin-left: 10px;
            font-weight: 300;
          }

         .faq_sec .container .contactForm {
          padding: 70px 50px;
          padding-left: 250px;
          margin-left: 130px;
          width: calc(100% - 180px);
          height: 100%;
          background: #fff;
          box-shadow: 0 50px 50px rgba(0, 0, 0, 0.25);
         }

          .faq_sec .container .contactForm h2 {
            color: #0f3959;
            font-size: 24px;
            font-weight: 700;
          }

          .faq_sec .container .contactForm .formBox {
            position: relative;
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            padding-top: 30px;
          }

          .faq_sec .container .contactForm .formBox .inputBox {
            position: relative;
            margin-bottom: 30px; /* Reduced margin to prevent excessive spacing */
          }

          .faq_sec .container .contactForm .formBox .inputBox.w50 {
            width: 47%;
          }

          .faq_sec .container .contactForm .formBox .inputBox.w100 {
            width: 100%;
          }

          .faq_sec .container .contactForm .formBox .inputBox input,
          .faq_sec .container .contactForm .formBox .inputBox textarea {
            width: 100%;
            resize: none;
            padding: 5px 0;
            font-size: 18px !important;
            font-weight: 300;
            color: #333;
            border: none;
            outline: none;
            border-bottom: 1px solid #777;

          }

          .faq_sec .container .contactForm .formBox .inputBox textarea {
            height: 70px;
          }

          .faq_sec .container .contactForm .formBox .inputBox span {
            position: absolute;
            left: 0;
            padding: 5px 0;
            pointer-events: none;
            font-size: 18px;
            font-weight: 300;
            transition: 0.3s;
          }


          .faq_sec .container .contactForm .formBox .inputBox input[type="submit"] {
            position: relative;
            cursor: pointer;
            background: #082341;
            color: #fff;
            border: none;
            max-width: 150px;
            padding: 12px;
          }

          .faq_sec .container .contactForm .formBox .inputBox input[type="submit"]:hover {
            background: #0d948b;
          }

          /* Responsive */
          @media (max-width: 1200px) {
            .faq_sec .container {
              width: 90%;
              min-width: auto;
              margin: 20px;
              box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
            }
            .faq_sec .container .contactInfo {
              top: 0;
              height: 550px;
              position: relative;
              box-shadow: none;
            }
            .faq_sec .container .contactForm {
              position: relative;
              width: calc(100% - 350px);
              padding-left: 0px;
              margin-left: 0;
              padding: 40px;
              height: 550px;
              box-shadow: none;
            }
          }

          @media (max-width: 991px) {

            .faq_sec::before {
              display: none;
            }
            .faq_sec .container {
              display: flex;
              flex-direction: column-reverse;
            }
            .faq_sec .container .contactForm {
              width: 100%;
              height: auto;
            }
            .faq_sec .container .contactInfo {
              width: 100%;
              height: auto;
              flex-direction: column;
            }
          }

          @media (max-width: 600px) {
            .faq_sec .container .contactForm {
              padding: 25px;
            }
            .faq_sec .container .contactInfo {
              padding: 25px;
              flex-direction: column;
            }
            .faq_sec .container .contactForm .formBox .inputBox.w50 {
              width: 100%;
            }
          }
          .captcha-line {
            display: flex;
            align-items: center;
            gap: 10px;
            flex-wrap: nowrap;
            margin-top: 10px;
          }

          .captcha-code-box {
            background-color: #f1f5f9;
            padding: 8px 14px;
            font-family: monospace;
            font-size: 1.2rem;
            font-weight: bold;
            letter-spacing: 2px;
            border-radius: 6px;
            border: 1px solid #ccc;
            min-width: 100px;
            text-align: center;
            user-select: none;
          }

          .refresh-btn {
            border: none;
            background: transparent;
            color: #082341;
            padding: 5px;
            transition: transform 0.2s ease;
          }

          .refresh-btn:hover {
            transform: rotate(90deg);
            color: #0d948b;
          }

          .faq_sec .icon-wrapper{
            margin-left: 0px;
          }

            .faq_sec .icon-wrapper{
             padding:0px;
             margin-left: 0px;
          }

          .faq_sec .social-card{
            margin-top: 0px !important;
            margin-bottom: 0px !important;
            padding: 0px;
          }

          .captcha-input {
            width: 140px;
            padding: 6px 10px;
            border-radius: 6px;
            border: 1px solid #ccc;
            font-size: 0.9rem;
            outline: none;
            transition: border 0.3s ease;
          }

          .captcha-input:focus {
            border-color: #095a55;
          }

          /* Ensure error messages don't disrupt layout */
          .inputBox small.text-danger {
            display: block;
            margin-top: 5px;
            font-size: 0.875rem;
            color: #dc3545;
          }
            .inputBox {
  position: relative;
}

.inputBox input {
  width: 100%;
  padding: 10px 0;
  font-size: 16px;
  border: none;
  border-bottom: 1px solid #777;
  background: transparent;
}

.inputBox span {
  position: absolute;
  left: 0;
  top: 10px;
  font-size: 16px;
  color: #777;
  pointer-events: none;
  transition: 0.3s ease all;
}

.inputBox.filled span,
.inputBox input:focus ~ span {
  transform: translateY(-25px);
            font-size: 12px !important;
            font-weight: 400;
            letter-spacing: 1px;
            color: #003d66;
}

        `}</style>
      </section>
    </>
  );
};

export default Contact;
